import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RequireRole } from "../../../../../../components/gate/RequireRole";
import { InlineEditableText } from "../../../../../../components/primitives/inputs/InlineEditableText";
import { CORPUS_FEED_DESCRIPTION_MAX_LENGTH } from "@shared/types/corpus/api.ts";
import type { CorpusFeedView } from "../../../../../../lib/corpusFeedView";
import { FeedAvatar } from "../../../../../../components/domain/data-display/FeedAvatar";
import { gaps } from "../../../../../../components/primitives/spacing";

const STATE_CHIP_COLOR = {
  active: "success",
  paused: "warning",
  retired: "default",
} as const;

interface FeedHeaderProps {
  view: CorpusFeedView;
  /** Saves a new description. An admin can change it in place; for anyone else it is plain text. */
  onSaveDescription: (description: string) => Promise<void>;
}

/** Who the feed is, under the page's own title: its initials, state, creation date and
 *  description. */
export function FeedHeader({ view, onSaveDescription }: FeedHeaderProps) {
  const { feed } = view;

  return (
    <Stack direction="row" gap={gaps.base} alignItems="center">
      <FeedAvatar view={view} />
      <Stack flex={1} minWidth={0} gap={gaps.tight}>
        <Stack direction="row" gap={gaps.snug} alignItems="center">
          <Chip label={view.stateLabel} color={STATE_CHIP_COLOR[feed.state]} />
          <Typography variant="caption" color="text.secondary">
            Created {new Date(feed.createdAt).toLocaleDateString()}
          </Typography>
        </Stack>
        <RequireRole
          role="platform.admin"
          fallback={
            feed.description ? <Typography variant="body2">{feed.description}</Typography> : null
          }
        >
          <Typography variant="body2" component="div">
            <InlineEditableText
              value={feed.description}
              label="Description"
              multiline
              maxLength={CORPUS_FEED_DESCRIPTION_MAX_LENGTH}
              placeholder="Add a description"
              onSave={onSaveDescription}
            />
          </Typography>
        </RequireRole>
      </Stack>
    </Stack>
  );
}
